import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { ProjectForm } from "@/components/ProjectForm";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { Select } from "@/components/Field";
import { Modal } from "@/components/Modal";
import type { CustomFieldType } from "@shared/types";

/**
 * Project Settings — owner-only. Consolidates project details editing, member
 * management (add / remove / transfer ownership), and a danger zone
 * (archive / delete). The "Settings" tab in the parent layout only renders for
 * the owner; this view also guards directly in case of a deep link, and every
 * mutation hits an owner-gated endpoint server-side.
 */
function SettingsPage() {
  const { projectId } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const projectQ = useQuery({
    queryKey: queryKeys.project(projectId),
    queryFn: () => api.getProject(projectId),
  });
  const meQ = useQuery({ queryKey: queryKeys.me(), queryFn: api.me });
  const membersQ = useQuery({
    queryKey: queryKeys.projectMembers(projectId),
    queryFn: () => api.projectMembers(projectId),
  });
  const teamQ = useQuery({
    queryKey: queryKeys.team(),
    queryFn: () => api.listTeam(),
  });

  const [addUserId, setAddUserId] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Member changes touch the roster, the project detail (owner_id), and the
  // projects list (owner/member previews) — invalidate all three.
  const invalidateMembers = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: queryKeys.projectMembers(projectId) }),
      qc.invalidateQueries({ queryKey: queryKeys.project(projectId) }),
      qc.invalidateQueries({ queryKey: queryKeys.projects() }),
    ]);

  const addMut = useMutation({
    mutationFn: (userId: string) => api.addProjectMember(projectId, userId),
    onSuccess: async () => {
      setAddUserId("");
      await invalidateMembers();
    },
  });
  const removeMut = useMutation({
    mutationFn: (userId: string) => api.removeProjectMember(projectId, userId),
    onSuccess: invalidateMembers,
  });
  const transferMut = useMutation({
    mutationFn: (userId: string) =>
      api.transferProjectOwnership(projectId, userId),
    onSuccess: invalidateMembers,
  });
  const archiveMut = useMutation({
    mutationFn: (status: "active" | "archived") =>
      api.patchProject(projectId, { status }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: queryKeys.projects() });
      await qc.invalidateQueries({ queryKey: queryKeys.myProjects() });
    },
  });
  const deleteMut = useMutation({
    mutationFn: () => api.deleteProject(projectId),
    onSuccess: async () => {
      // Navigate away first so this page and the parent project layout unmount
      // before any refetch fires. Then refresh only the EXACT list keys — a
      // broad ["projects"] prefix invalidation would refetch the just-deleted
      // project's detail (["projects", id]) and 403/404 mid-unmount.
      navigate({ to: "/projects" });
      await qc.invalidateQueries({
        queryKey: queryKeys.projects(),
        exact: true,
      });
      await qc.invalidateQueries({ queryKey: queryKeys.myProjects() });
    },
  });

  if (projectQ.isLoading) {
    return (
      <div className="proj-shell-content">
        <p>Loading…</p>
      </div>
    );
  }
  const proj = projectQ.data?.project;
  if (!proj) {
    return (
      <div className="proj-shell-content">
        <p>Project not found.</p>
      </div>
    );
  }

  const myId = meQ.data?.user?.id;
  const isOwner = myId != null && myId === proj.ownerId;
  if (!isOwner) {
    return (
      <div className="proj-shell-content">
        <div className="panel">
          <div className="panel-body">
            <p className="t-muted">
              Only the project owner can manage settings.{" "}
              <Link to="/projects/$projectId" params={{ projectId }}>
                Back to the board
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    );
  }

  const members = membersQ.data?.members ?? [];
  const memberIds = new Set(members.map((m) => m.userId));
  const addable = (teamQ.data?.members ?? []).filter(
    (u) => !memberIds.has(u.id),
  );
  const isArchived = proj.status === "archived";

  return (
    <div
      className="proj-shell-content"
      style={{ display: "grid", gap: 24, maxWidth: 720 }}
    >
      {/* Details */}
      <section className="panel">
        <div className="panel-head">
          <h3>Project details</h3>
        </div>
        <div className="panel-body">
          <ProjectForm
            submitLabel="Save changes"
            initial={{
              name: proj.name,
              description: proj.description ?? "",
              kind: proj.kind,
              status: proj.status,
              color: proj.color ?? undefined,
              targetDate: proj.targetDate ? proj.targetDate.slice(0, 10) : "",
              tagNames: (proj.tags ?? []).map((t) => t.name),
            }}
            onSubmit={async (v) => {
              await api.patchProject(proj.id, {
                name: v.name,
                description: v.description,
                kind: v.kind,
                status: v.status,
                color: v.color,
                targetDate: v.targetDate || null,
                tagNames: v.tagNames,
              });
              await qc.invalidateQueries({
                queryKey: queryKeys.project(proj.id),
              });
              await qc.invalidateQueries({ queryKey: queryKeys.projects() });
              await qc.invalidateQueries({ queryKey: queryKeys.myProjects() });
              await qc.invalidateQueries({ queryKey: queryKeys.tags() });
            }}
          />
        </div>
      </section>

      {/* Members */}
      <section className="panel">
        <div className="panel-head">
          <h3>Members</h3>
        </div>
        <div className="panel-body">
          <ul
            className="members-list"
            style={{ listStyle: "none", padding: 0, margin: 0 }}
          >
            {members.map((m) => (
              <li
                key={m.userId}
                className="member-row"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 0",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                <Avatar id={m.userId} name={m.name ?? m.email} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>{m.name ?? m.email}</div>
                  <div className="t-muted" style={{ fontSize: 13 }}>
                    {m.role === "owner" ? "Owner" : "Member"} · {m.email}
                  </div>
                </div>
                {m.role !== "owner" && (
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={transferMut.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Make ${m.name ?? m.email} the owner? You'll become a member and lose owner controls.`,
                          )
                        )
                          transferMut.mutate(m.userId);
                      }}
                    >
                      Make owner
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={removeMut.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove ${m.name ?? m.email} from this project?`,
                          )
                        )
                          removeMut.mutate(m.userId);
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>

          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 16,
              alignItems: "center",
            }}
          >
            <Select
              value={addUserId}
              onChange={(e) => setAddUserId(e.target.value)}
              aria-label="Add member"
              style={{ flex: 1 }}
            >
              <option value="">Add a member…</option>
              {addable.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name ?? u.email}
                </option>
              ))}
            </Select>
            <Button
              variant="primary"
              size="sm"
              disabled={!addUserId || addMut.isPending}
              onClick={() => addMut.mutate(addUserId)}
            >
              Add
            </Button>
          </div>
          {addable.length === 0 && (
            <p className="t-muted" style={{ fontSize: 13, marginTop: 8 }}>
              Everyone on the team is already a member.
            </p>
          )}
          {(addMut.isError || removeMut.isError || transferMut.isError) && (
            <p role="alert" className="t-danger" style={{ marginTop: 8 }}>
              Couldn&apos;t update members. Try again.
            </p>
          )}
        </div>
      </section>

      {/* Custom fields */}
      <CustomFieldsSection projectId={projectId} />

      {/* Team associations */}
      <ProjectTeamsSection projectId={projectId} />

      {/* Danger zone */}
      <section className="panel">
        <div className="panel-head">
          <h3 className="t-danger">Danger zone</h3>
        </div>
        <div className="panel-body" style={{ display: "grid", gap: 16 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontWeight: 500 }}>
                {isArchived ? "Unarchive project" : "Archive project"}
              </div>
              <div className="t-muted" style={{ fontSize: 13 }}>
                {isArchived
                  ? "Make this project active again."
                  : "Hide it from active lists. Tasks and data are kept."}
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={archiveMut.isPending}
              onClick={() =>
                archiveMut.mutate(isArchived ? "active" : "archived")
              }
            >
              {isArchived ? "Unarchive" : "Archive"}
            </Button>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div>
              <div style={{ fontWeight: 500 }}>Delete project</div>
              <div className="t-muted" style={{ fontSize: 13 }}>
                Permanently delete this project and all its tasks, comments, and
                files. This cannot be undone.
              </div>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={() => setDeleteOpen(true)}
            >
              Delete…
            </Button>
          </div>
        </div>
      </section>

      <Modal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete project"
        width={420}
      >
        <p>
          Permanently delete <strong>{proj.name}</strong> and everything in it?
          This cannot be undone.
        </p>
        {deleteMut.isError && (
          <p role="alert" className="t-danger">
            Couldn&apos;t delete. Try again.
          </p>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 16,
          }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            disabled={deleteMut.isPending}
            onClick={() => deleteMut.mutate()}
          >
            {deleteMut.isPending ? "Deleting…" : "Delete project"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "select", label: "Single select" },
  { value: "multi_select", label: "Multi select" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Checkbox" },
];

/** Owner-only custom-field definition manager for a project. */
function CustomFieldsSection({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const fieldsQ = useQuery({
    queryKey: queryKeys.customFields(projectId),
    queryFn: () => api.listCustomFields(projectId),
  });
  const [name, setName] = useState("");
  const [type, setType] = useState<CustomFieldType>("text");
  const [options, setOptions] = useState("");
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.customFields(projectId) });

  const create = useMutation({
    mutationFn: () =>
      api.createCustomField(projectId, {
        name: name.trim(),
        type,
        options:
          type === "select" || type === "multi_select"
            ? options
                .split(",")
                .map((o) => o.trim())
                .filter(Boolean)
            : undefined,
      }),
    onSuccess: () => {
      invalidate();
      setName("");
      setOptions("");
      setType("text");
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.deleteCustomField(id),
    onSuccess: invalidate,
  });

  const fields = fieldsQ.data?.fields ?? [];
  const needsOptions = type === "select" || type === "multi_select";

  return (
    <section className="panel">
      <div className="panel-head">
        <h3>Custom fields</h3>
      </div>
      <div className="panel-body" style={{ display: "grid", gap: 14 }}>
        {fields.length === 0 ? (
          <p className="t-muted" style={{ fontSize: 13 }}>
            No custom fields yet. Add domain-specific fields to this project’s
            tasks below.
          </p>
        ) : (
          <ul className="cf-def-list">
            {fields.map((f) => (
              <li key={f.id} className="cf-def-row">
                <span style={{ flex: 1 }}>
                  <strong>{f.name}</strong>{" "}
                  <span className="t-muted t-sm">
                    {FIELD_TYPES.find((t) => t.value === f.type)?.label ??
                      f.type}
                    {f.options ? ` · ${f.options.join(", ")}` : ""}
                  </span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (
                      confirm(
                        `Delete the "${f.name}" field? All values stored for it on every task will be permanently removed.`,
                      )
                    )
                      remove.mutate(f.id);
                  }}
                >
                  Delete
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            flexWrap: "wrap",
          }}
        >
          <input
            className="input"
            placeholder="Field name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Custom field name"
            style={{ flex: "1 1 160px" }}
          />
          <select
            className="select"
            value={type}
            onChange={(e) => setType(e.target.value as CustomFieldType)}
            aria-label="Custom field type"
          >
            {FIELD_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {needsOptions && (
            <input
              className="input"
              placeholder="Options (comma-separated)"
              value={options}
              onChange={(e) => setOptions(e.target.value)}
              aria-label="Custom field options"
              style={{ flex: "1 1 200px" }}
            />
          )}
          <Button
            variant="primary"
            size="sm"
            disabled={
              create.isPending ||
              !name.trim() ||
              (needsOptions && !options.trim())
            }
            onClick={() => create.mutate()}
          >
            Add field
          </Button>
        </div>
        {create.isError && (
          <p className="t-danger t-sm">Couldn’t add the field. Try again.</p>
        )}
      </div>
    </section>
  );
}

/** Owner-only: associate the project with one or more workspace teams. */
function ProjectTeamsSection({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const teamsQ = useQuery({ queryKey: ["teams"], queryFn: () => api.teams() });
  const assignedQ = useQuery({
    queryKey: ["project-teams", projectId],
    queryFn: () => api.projectTeams(projectId),
  });
  const assigned = new Set((assignedQ.data?.teams ?? []).map((t) => t.id));
  const save = useMutation({
    mutationFn: (teamIds: string[]) => api.setProjectTeams(projectId, teamIds),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["project-teams", projectId] }),
  });
  const allTeams = teamsQ.data?.teams ?? [];

  return (
    <section className="panel">
      <div className="panel-head">
        <h3>Teams</h3>
      </div>
      <div className="panel-body">
        {allTeams.length === 0 ? (
          <p className="t-muted" style={{ fontSize: 13 }}>
            No teams exist yet. An admin can create teams from the Team page.
          </p>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {allTeams.map((t) => (
              <label
                key={t.id}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <input
                  type="checkbox"
                  checked={assigned.has(t.id)}
                  disabled={save.isPending}
                  onChange={(e) => {
                    const next = new Set(assigned);
                    if (e.target.checked) next.add(t.id);
                    else next.delete(t.id);
                    save.mutate([...next]);
                  }}
                />
                {t.name}
              </label>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export const Route = createFileRoute("/projects/$projectId/settings")({
  component: SettingsPage,
});
